import React, { useEffect, useContext, useCallback, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { validateAdUrl } from '@bingads-webui-campaign-react/url-input-fluent';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { constants } from '@bingads-webui-campaign-react/image-experience';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { ValidatedImageExperienceWrapper } from '@bingads-webui-campaign-react/pmax-image-experience';
import { useWebsiteSuggestion } from '@bingads-webui-campaign-react/add-image-hooks';
import { getUnifiedAdPreviewConfig } from '../get-unified-ad-preview-config';

export const ImageExperienceWithSuggestionPollerForBulk = ({
  campaignId,
  oDataConfig,
  data,
  ctaText,
  url,
  setUrl,
  permissions,
  perfMarker,
  imagesContainerRef,
  logosContainerRef,
  genericImage,
  previewImagesSharedState,
  setPreviewImagesSharedState,
  setImageExperiencePortalContainerState,
  setImageExperienceAddNewImageCallback,
}) => {
  const i18n = useContext(I18nContext);
  const instrumentation = useContext(InstrumentationContext);
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext);
  const activityContext = useContext(ActivityContext);
  const scenario = useContext(ScenarioContext);
  const [, setRecommendationContext] = useContext(RecommendationContext);
  const { aid } = scope.values || {};
  const adPreviewConfig = getUnifiedAdPreviewConfig({ isMultimediaAd: true });
  const businessName = data.BusinessName;
  const images = data.Images;
  const imageData = useMemo(() => ({
    Images: images,
    BusinessName: businessName,
    CallToAction: ctaText,
    FinalUrls: [url],
  }), [businessName, ctaText, images, url]);

  const {
    suggestionState,
    suggestionPoller,
  } = useWebsiteSuggestion({
    oDataConfig,
    adContext: {
      advertiserCustomerId: _.get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID']),
      accountId: aid,
      campaignId,
    },
    instrumentation,
    logActivity: activityContext.signal,
    permissions,
    fullUrl: url,
    hasErrorInUrl: validateAdUrl(url) !== null,
    adCreatorScenario: {
      scenarioName: `${scenario.name}AdCreation`,
      scenarioValue: `${scenario.name}AdCreation`,
    },
    customizedGetMinimumSizeForImage: _.partial(constants.getMinimumSizeForImage, null, true),
  });

  const updateFinalUrlCallback = useCallback((newFinalUrls) => {
    setUrl(newFinalUrls[0]);
  }, []);

  useEffect(() => {
    if (suggestionState && suggestionState.suggestion && suggestionState.suggestion.images) {
      setRecommendationContext({
        images: {
          websiteImagesState: {
            suggestion: suggestionState.suggestion,
          },
        },
      });
    }
  }, [setRecommendationContext, suggestionState]);

  return (
    <ValidatedImageExperienceWrapper
      i18n={i18n}
      permissions={permissions}
      campaignId={campaignId}
      imagesContaineRef={imagesContainerRef}
      logosContaineRef={logosContainerRef}
      isLogoEnabled
      isBoostPortraitEnabled={false}
      updateFinalUrlCallback={updateFinalUrlCallback}
      previewImagesSharedState={previewImagesSharedState}
      setPreviewImagesSharedState={setPreviewImagesSharedState}
      updatePortalContainerState={setImageExperiencePortalContainerState}
      setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
      validationProps={{ data: imageData, initialValue: imageData }}
      suggestionPoller={suggestionPoller}
      isAd
      wizardPerfMarker={perfMarker}
      isCombinedMsanAndMmaAspectRatios={false}
      customMaxImageCount={constants.DEFAULT_MAX_IMAGE}
      UAPLPMaxIntegration={permissions.dynamic.AdPreviewLibrarySearchMMA}
      isMSANBoostEnabled={false} // ??
      isImproveMMA={false} // ??
      isMultimediaAd
      isMultimediaAdinMSANEnabled={false}
      imageErrorMessages={[]} // ??
      genericImage={genericImage}
      adPreviewConfig={adPreviewConfig}
      isBulkEditMMA
    />
  );
};

ImageExperienceWithSuggestionPollerForBulk.propTypes = {
  data: PropTypes.shape({
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
    MobileFinalUrls: PropTypes.string,
    BusinessName: PropTypes.string,
    Headlines: PropTypes.arrayOf(PropTypes.string),
    LongHeadlines: PropTypes.arrayOf(PropTypes.string),
    Descriptions: PropTypes.arrayOf(PropTypes.string),
    Images: PropTypes.shape({
      ImageList: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    TrackingUrlTemplate: PropTypes.string,
    FinalUrlSuffix: PropTypes.string,
    UrlCustomParameters: PropTypes.string,
    FinalUrlForRecommendation: PropTypes.string,
    CallToAction: PropTypes.shape({
      cta: PropTypes.string,
      language: PropTypes.string,
    }),
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AdPreviewLibrarySearchMMA: PropTypes.bool,
    }),
  }).isRequired,
  genericImage: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  ctaText: PropTypes.string.isRequired,
  imagesContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  logosContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  previewImagesSharedState: PropTypes.shape({}).isRequired,
  setPreviewImagesSharedState: PropTypes.func.isRequired,
  setImageExperiencePortalContainerState: PropTypes.func.isRequired,
  setImageExperienceAddNewImageCallback: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
  setUrl: PropTypes.func.isRequired,
};
